import {
  IAddToCartBundleInfo,
  IAddToCartItem,
  IAddToCartItemAttribute,
  ICartBundlePromotionInfo,
  ICartItems,
  IGetCartProduct,
  IGuestCartItem,
  IGuestCartResponse,
} from '@/interfaces/cart';
import { getGuestCart } from '@/services/cart.service';

const GUEST_CART_STORAGE_KEY = 'cart';

const isBrowser = () => typeof window !== 'undefined';

export const createEmptyCart = (): IGetCartProduct => ({
  Items: [],
  SubTotalValue: 0,
  TotalValue: 0,
  Warnings: [],
});

export const readGuestCart = (): IGetCartProduct => {
  if (!isBrowser()) return createEmptyCart();
  try {
    const raw = localStorage.getItem(GUEST_CART_STORAGE_KEY);
    if (!raw) return createEmptyCart();
    const parsed = JSON.parse(raw) as IGetCartProduct;
    return parsed?.Items ? parsed : createEmptyCart();
  } catch {
    return createEmptyCart();
  }
};

export const writeGuestCart = (cart: IGetCartProduct): void => {
  if (!isBrowser()) return;
  localStorage.setItem(GUEST_CART_STORAGE_KEY, JSON.stringify(cart));
};

export const getCartItemProductId = (cartItem: ICartItems): string => cartItem.ProductId || cartItem.Id.split('_')[0];

export const getCartItemAttributes = (cartItem: ICartItems): IAddToCartItemAttribute[] => {
  if (cartItem.Attributes?.length) return cartItem.Attributes;

  const selectedVariant = cartItem.Variations?.find((x) => x.IsSelected);
  return selectedVariant ? [{ key: selectedVariant.ProductAttributeId, value: selectedVariant.ProductAttributeValueId }] : [];
};

export const getCartItemBundlePromotionInfo = (cartItem: ICartItems): ICartBundlePromotionInfo | null => {
  const bundleInfo = cartItem.CartBundleInfoDto;
  if (!bundleInfo?.BundleId || !bundleInfo.BundlePromotionId || !bundleInfo.SlabId || !bundleInfo.Role) return null;

  return {
    bundleId: bundleInfo.BundleId,
    bundlePromotionId: bundleInfo.BundlePromotionId,
    slabId: bundleInfo.SlabId,
    role: bundleInfo.Role,
  };
};

export const toGuestCartItems = (cart: IGetCartProduct): IGuestCartItem[] =>
  (cart?.Items ?? []).map((cartItem) => ({
    id: cartItem.Id,
    productId: getCartItemProductId(cartItem),
    quantity: cartItem.Quantity,
    attributes: getCartItemAttributes(cartItem),
    cartBundlePromotionInfo: getCartItemBundlePromotionInfo(cartItem),
  }));

const toCartFromGuestResponse = (response: IGuestCartResponse, previousCart: IGetCartProduct): IGetCartProduct => {
  const { CartItems, ...cart } = response;

  return {
    ...cart,
    Items: (response.Items ?? []).map((item) => {
      const previousItem = previousCart.Items.find((x) => x.Id === item.Id);
      const guestCartItem = CartItems?.find((x) => x.Id === item.Id);

      return {
        ...item,
        AvailableColors: previousItem?.AvailableColors ?? [],
        IsGiftVoucher: previousItem?.IsGiftVoucher ?? false,
        Attributes: guestCartItem?.Attributes?.map((attribute) => ({ key: attribute.Key, value: attribute.Value })) ?? [],
      };
    }),
  };
};

export const syncGuestCart = async (storeIds?: string[], addtoCartBundleInfo?: IAddToCartBundleInfo): Promise<IGetCartProduct> => {
  const localCart = readGuestCart();
  if (!localCart.Items.length && !addtoCartBundleInfo) return localCart;

  const response = await getGuestCart({
    cartItems: toGuestCartItems(localCart),
    addtoCartBundleInfo,
    storeIds: storeIds?.filter(Boolean) ?? [],
  });

  const cart = toCartFromGuestResponse(response, localCart);
  writeGuestCart(cart);

  return cart;
};

export const toAddToCartPayloads = (cart: IGetCartProduct): IAddToCartItem[] => {
  const bundleGroups = new Map<string, { bundleInfo: ICartBundlePromotionInfo; items: ICartItems[] }>();
  const payloads: IAddToCartItem[] = [];

  (cart?.Items ?? []).forEach((cartItem) => {
    const bundleInfo = getCartItemBundlePromotionInfo(cartItem);

    if (!bundleInfo) {
      payloads.push({
        productId: getCartItemProductId(cartItem),
        quantity: cartItem.Quantity,
        attributes: getCartItemAttributes(cartItem),
      });
      return;
    }

    const group = bundleGroups.get(bundleInfo.bundleId) ?? { bundleInfo, items: [] };
    group.items.push(cartItem);
    bundleGroups.set(bundleInfo.bundleId, group);
  });

  bundleGroups.forEach(({ bundleInfo, items }) => {
    const bundleItems = items.map((cartItem) => ({
      productId: getCartItemProductId(cartItem),
      quantity: cartItem.Quantity,
      attributes: getCartItemAttributes(cartItem),
      itemRole: getCartItemBundlePromotionInfo(cartItem)?.role ?? bundleInfo.role,
    }));

    payloads.push({
      productId: bundleItems[0].productId,
      quantity: bundleItems[0].quantity,
      attributes: bundleItems[0].attributes,
      addtoCartBundleInfo: {
        bundlePromotionId: bundleInfo.bundlePromotionId,
        slabId: bundleInfo.slabId,
        items: bundleItems,
      },
    });
  });

  return payloads;
};

export const toStandaloneAddToCartPayloads = (payload: IAddToCartItem): IAddToCartItem[] =>
  payload.addtoCartBundleInfo
    ? payload.addtoCartBundleInfo.items.map((item) => ({ productId: item.productId, quantity: item.quantity, attributes: item.attributes }))
    : [payload];
